import * as React from 'react';
import { useState, useEffect } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableContainer from '@mui/material/TableContainer';
import PropTypes from 'prop-types';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import DownloadIcon from '@mui/icons-material/Download';
import CircularProgress from '@mui/material/CircularProgress';
import SharePointService from '../services/sharepoint.service';

function CustomTabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}
CustomTabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

export default function CommonDocuments() {
  const theme = useTheme();
  const isMdDown = useMediaQuery(theme.breakpoints.down('md'));

  const [value, setValue] = React.useState(0);
  const handleChange = (_event, newValue) => setValue(newValue);

  const [files, setFiles] = useState([]);
  const [working, setWorking] = useState(false);

  const GetSPFiles = async () => {
    setWorking(true);
    const res = await SharePointService.getCommonDocuments();
    setFiles(res);
    setWorking(false);
  };

  const SPfetchFile = async (file) => {
    try {
      const SPfile = await SharePointService.getDownloadCommonFile(file);
      const blob = new Blob([SPfile], { type: 'application/pdf' });
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.setAttribute('download', file.name);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error downloading file:', error);
    }
  };

  const SPopenFile = async (file) => {
    try {
      const SPfile = await SharePointService.getDownloadFile(file);
      const blob = new Blob([SPfile], { type: 'application/pdf' });
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error downloading file:', error);
    }
  };

  useEffect(() => {
    GetSPFiles().catch(console.error);
  }, []);

  let content = (
    <Typography variant="h6" mt={4}>
      Keine Dateien zur Anzeige gefunden, bitte melden Sie sich bei Ihrer Kundenmanagerin.
    </Typography>
  );

  if (files?.message !== 'Wrong Secret' && Array.isArray(files) && files.length) {
    content = (
      <>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={value}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            onChange={handleChange}
            aria-label="Dokumenten-Tabs"
          >
            {files.map((folder, idx) => (
              <Tab key={`${folder.category}-${idx}`} label={folder.category} id={`simple-tab-${idx}`} aria-controls={`simple-tabpanel-${idx}`} />
            ))}
          </Tabs>
        </Box>

        {files.map((folder, idx) => (
          <CustomTabPanel key={`panel-${folder.category}-${idx}`} value={value} index={idx}>
            <TableContainer>
              <Table size="small" sx={{ minWidth: 150 }}>
                {/* Header nur auf Desktop */}
                {!isMdDown && (
                  <TableHead>
                    <TableRow>
                      <TableCell align="left" sx={{ fontWeight: 'bold', '@media screen and (min-width: 960px)': { width: '50%' } }}>
                        Dateiname
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                        Letzte Änderung
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold', width: 100 }}>
                        Download
                      </TableCell>
                    </TableRow>
                  </TableHead>
                )}

                <TableBody>
                  {folder.files.files.map((file, fIdx) =>
                    !isMdDown ? (
                      // Desktop-Zeile
                      <TableRow key={`${file.name}-${fIdx}`}>
                        <TableCell
                          onClick={() => SPopenFile(file)}
                          sx={{ '&:hover': { cursor: 'pointer' }, '@media screen and (min-width: 960px)': { width: '50%' } }}
                        >
                          {file.name}
                        </TableCell>
                        <TableCell align="right">{file.edited}</TableCell>
                        <TableCell align="right" sx={{ width: 100 }}>
                          <IconButton
                            aria-label="download"
                            onClick={(e) => {
                              e.stopPropagation();
                              SPfetchFile(file);
                            }}
                          >
                            <DownloadIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ) : (
                      // Mobile/Tablet-Zeile (kompakt)
                      <TableRow key={`${file.name}-${fIdx}`}>
                        <TableCell colSpan={3} sx={{ py: 1.25 }}>
                          <Box
                            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, cursor: 'pointer' }}
                            onClick={() => SPopenFile(file)}
                          >
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {file.name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {file.edited}
                              </Typography>
                            </Box>
                            <IconButton
                              aria-label="download"
                              onClick={(e) => {
                                e.stopPropagation();
                                SPfetchFile(file);
                              }}
                            >
                              <DownloadIcon />
                            </IconButton>
                          </Box>
                        </TableCell>
                      </TableRow>
                    )
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CustomTabPanel>
        ))}
      </>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      {working ? (
        <Box py={10}>
          <CircularProgress />
        </Box>
      ) : (
        content
      )}
    </Box>
  );
}
